/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { format } from 'timeago.js';

import Table from 'app/components/ui/Table';
import s from 'app/components/ui/Table/Table.module.sass';
import Select from 'app/components/ui/Select';
import { TZKT_EXPLORER_URL } from 'app/utils/defaults';
import Link from 'app/components/common/Link';
import { getSlice, shortize, transformMutezToTez } from 'app/utils/helpers';
import { TransactionsScopeEnum } from 'app/types/transactions';
import { useCurrency } from 'app/context/CurrencyContext';
import { ReactComponent as TezosSlim } from 'app/components/svg/TezosSlim.svg';
import { CurrencyEnum } from 'app/components/ui/CurrencySwitcher/CurrencySwitcher';
import { PairActionType } from 'app/generated/graphql';

import { items, TransactionSelectItemsEnum } from './select';

type TransactionsTableProps = {
  data?: any
  fetchCurrentType?: any
  tokenIds?: string[]
  pairId?: string
  transactionsByToken?: boolean
  transactionsByPair?: boolean
  scope: TransactionsScopeEnum
};

const TransactionsTable: React.FC<TransactionsTableProps> = ({
  data,
  fetchCurrentType,
  tokenIds,
  pairId,
  transactionsByToken,
  transactionsByPair,
  scope,
}) => {
  const { currency, xtzUsdQuote } = useCurrency();

  const [offset, setOffset] = useState(0);

  const [activeItem, setActiveItem] = useState<any>(
    TransactionSelectItemsEnum.ALL,
  );

  const [pageCount, setPageCount] = useState<number>(0);

  const [trxType, setTrxType] = useState<any>(TransactionSelectItemsEnum.ALL);

  useEffect(() => {
    if (data) {
      setPageCount(data.transactions?.totalCount!);
    }
  }, [data]);

  useEffect(() => {
    if (activeItem === TransactionSelectItemsEnum.SWAP_1_TO_2) {
      setTrxType(PairActionType.Swap1to2);
      return undefined;
    }

    if (activeItem === TransactionSelectItemsEnum.SWAP_2_TO_1) {
      setTrxType(PairActionType.Swap2to1);
      return undefined;
    }

    setTrxType(activeItem);
    return undefined;
  }, [activeItem]);

  useEffect(() => {
    setOffset(0);
  }, [activeItem]);

  useEffect(() => {
    if (transactionsByToken) {
      fetchCurrentType({
        variables: {
          address: tokenIds && tokenIds.length && tokenIds[0],
          tokenId: tokenIds && tokenIds.length && tokenIds[1],
          limit: 10,
          offset,
          scope,
          trxType,
        },
      });
      return undefined;
    }

    if (transactionsByPair) {
      fetchCurrentType({
        variables: {
          address: pairId,
          limit: 10,
          offset,
          scope,
          trxType,
        },
      });
      return undefined;
    }

    fetchCurrentType({
      variables: {
        limit: 10,
        offset,
        scope,
        trxType,
      },
    });
    return undefined;
  }, [trxType, fetchCurrentType, offset, pairId, transactionsByPair, transactionsByToken, scope, tokenIds]);

  const columns = useMemo(() => [
    {
      Header: () => (
        <div
          className={cx(s.cell)}
        >
          <Select
            items={items}
            handleSelect={fetchCurrentType}
            setActiveItem={setActiveItem}
            activeItem={activeItem}
          />
        </div>
      ),
      id: 'trxType',
      accessor: (row: any) => {
        if (row.node.trxType === PairActionType.Swap1to2) {
          return (
            <div
              className={cx(s.cell, s.link)}
            >
              <a href={`${TZKT_EXPLORER_URL}/${row.node.id}`} target="_blank" rel="noreferrer">
                {`Swap ${row.node.pair.token1.symbol} for ${getSlice(row.node.pair.token2.symbol)}`}
              </a>
            </div>
          );
        }

        if (row.node.trxType === PairActionType.Swap2to1) {
          return (
            <div
              className={cx(s.cell, s.link)}
            >
              <a href={`${TZKT_EXPLORER_URL}/${row.node.id}`} target="_blank" rel="noreferrer">
                {`Swap ${getSlice(row.node.pair.token2.symbol)} for ${row.node.pair.token1.symbol}`}
              </a>
            </div>
          );
        }

        if (row.node.trxType === PairActionType.Add) {
          return (
            <div
              className={cx(s.cell, s.link)}
            >
              <a href={`${TZKT_EXPLORER_URL}/${row.node.id}`} target="_blank" rel="noreferrer">
                {`Add ${row.node.pair.token1.symbol} and ${getSlice(row.node.pair.token2.symbol)}`}
              </a>
            </div>
          );
        }

        if (row.node.trxType === PairActionType.Remove) {
          return (
            <div
              className={cx(s.cell, s.link)}
            >
              <a href={`${TZKT_EXPLORER_URL}/${row.node.id}`} target="_blank" rel="noreferrer">
                {`Remove ${row.node.pair.token1.symbol} and ${getSlice(row.node.pair.token2.symbol)}`}
              </a>
            </div>
          );
        }
        return (
          <div
            className={cx(s.cell, s.link)}
          >
            <a href={`${TZKT_EXPLORER_URL}/${row.node.id}`} target="_blank" rel="noreferrer">
              {row.node.trxType}
            </a>
          </div>
        );
      },
    },
    {
      Header: 'Total value',
      id: 'xtzUsdQuoteHistorical',
      accessor: (row: any) => {
        let transactionSum = 0;
        if (row.node.trxType === PairActionType.Swap1to2 || row.node.trxType === PairActionType.Swap2to1) {
          transactionSum = row.node.token1Amount * (currency === CurrencyEnum.USD ? row.node.xtzUsdQuoteHistorical : 1);
        }
        if (row.node.trxType === PairActionType.Add || row.node.trxType === PairActionType.Remove) {
          transactionSum = row.node.token1Amount * 2 * (currency === CurrencyEnum.USD ? row.node.xtzUsdQuoteHistorical : 1);
        }

        return (
          <div
            className={cx(s.cell)}
          >
            {currency === CurrencyEnum.USD
              ? '$' : (
                <TezosSlim className={cx(s.tezos)} />
              )}
            {' '}
            {transformMutezToTez({
              value: transactionSum,
              fraction: 3,
              xtzUsdQuote,
              currency,
            })}
          </div>
        );
      },
    },
    {
      Header: 'Token Amount 1',
      id: 'token1Amount',
      accessor: (row: any) => {
        if (row.node.trxType === PairActionType.Swap2to1) {
          return (
            <div
              className={cx(s.cell)}
            >
              {transformMutezToTez({
                value: row.node.token2Amount,
                fraction: 6,
                dec: row.node.pair.token2.decimals,
              })}
              {' '}
              {getSlice(row.node.pair.token2.symbol)}
            </div>
          );
        }

        return (
          <div
            className={cx(s.cell)}
          >
            <TezosSlim className={cx(s.tezos)} />
            {transformMutezToTez({
              value: row.node.token1Amount,
              fraction: 6,
              dec: row.node.pair.token1.decimals,
            })}
          </div>
        );
      },
    },
    {
      Header: 'Token Amount 2',
      id: 'token2Amount',
      accessor: (row: any) => {
        if (row.node.trxType === PairActionType.Swap2to1) {
          return (
            <div
              className={cx(s.cell)}
            >
              <TezosSlim className={cx(s.tezos)} />
              {transformMutezToTez({
                value: row.node.token1Amount,
                fraction: 6,
                dec: row.node.pair.token1.decimals,
              })}
            </div>
          );
        }

        return (
          <div
            className={cx(s.cell)}
          >
            {transformMutezToTez({
              value: row.node.token2Amount,
              fraction: 6,
              dec: row.node.pair.token2.decimals,
            })}
            {' '}
            {getSlice(row.node.pair.token2.symbol)}
          </div>
        );
      },
    },
    {
      Header: 'Account',
      id: 'sender',
      accessor: (row: any) => (
        <div
          className={cx(s.cell, s.link)}
        >
          <a href={`${TZKT_EXPLORER_URL}/${row.node.sender}`} target="_blank" rel="noreferrer">
            {shortize(row.node.sender)}
          </a>
          <Link value={row.node.sender} className={cx(s.svgLink)} />
        </div>
      ),
    },
    {
      Header: 'Time',
      id: 'timestamp',
      accessor: (row: any) => {
        const getDateFromData = new Date(row.node.timestamp);
        const getCurrentTime = getDateFromData.getTime();

        return (
          <div
            className={cx(s.cell)}
          >
            {format(getCurrentTime)}
          </div>
        );
      },
    },
  ], [activeItem, currency, fetchCurrentType, xtzUsdQuote]);

  return (
    <Table
      columns={columns}
      data={data?.transactions?.edges ?? []}
      pageCount={pageCount}
      theme="transactions"
      setOffset={setOffset}
      activeItem={activeItem}
    />
  );
};

export default TransactionsTable;
